import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { buildScholarlabLaunchUrl, fetchScholarlabSimulations } from '../../lib/scholarlabSso';
import './Scholarlab.css';

function getStoredUser() {
  let raw = sessionStorage.getItem('currentUser');
  if (!raw) {
    raw = localStorage.getItem('scholarlab_currentUser');
    if (raw) {
      sessionStorage.setItem('currentUser', raw);
      localStorage.removeItem('scholarlab_currentUser');
    }
  }
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function getTopicList(topics) {
  return String(topics || '')
    .split('|')
    .map((topic) => topic.trim())
    .filter(Boolean)
    .slice(0, 4);
}

export default function Scholarlab() {
  const [currentUser, setCurrentUser] = useState(() => getStoredUser());
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [activeSubject, setActiveSubject] = useState('All');
  const [launchingId, setLaunchingId] = useState(null);

  const loadSimulations = useCallback(() => {
    const user = getStoredUser();
    setCurrentUser(user);

    if (!user?.id) {
      setError('Please sign in again to access Scholarlab.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    fetchScholarlabSimulations(user.id)
      .then((data) => {
        console.info('[Scholarlab] Catalog page received simulations', {
          userId: user.id,
          count: data.simulations?.length || 0,
        });
        setSimulations(data.simulations || []);
        setError('');
      })
      .catch((err) => {
        setError(err.message || 'Failed to load Scholarlab simulations.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadSimulations();
  }, [loadSimulations]);

  const subjects = useMemo(() => {
    const names = new Set();
    simulations.forEach((simulation) => {
      const id = simulation.customUseCaseId || simulation.id || '';
      if (id.startsWith('SC')) names.add('Chemistry');
      else if (id.startsWith('SB')) names.add('Biology');
      else if (id.startsWith('SP')) names.add('Physics');
    });
    return ['All', ...Array.from(names).sort()];
  }, [simulations]);

  const filteredSimulations = useMemo(() => {
    const q = query.trim().toLowerCase();
    return simulations.filter((simulation) => {
      const subjectMatch =
        activeSubject === 'All' ||
        (activeSubject === 'Chemistry' && String(simulation.customUseCaseId).startsWith('SC')) ||
        (activeSubject === 'Biology' && String(simulation.customUseCaseId).startsWith('SB')) ||
        (activeSubject === 'Physics' && String(simulation.customUseCaseId).startsWith('SP'));

      if (!subjectMatch) return false;
      if (!q) return true;

      return [
        simulation.module,
        simulation.topics,
        simulation.description,
        simulation.customUseCaseId,
      ].some((value) => String(value || '').toLowerCase().includes(q));
    });
  }, [activeSubject, query, simulations]);

  const openSimulation = async (simulation) => {
    if (!currentUser?.id) return;

    try {
      console.info('[Scholarlab] Simulation card clicked', {
        userId: currentUser.id,
        simulationId: simulation.customUseCaseId || simulation.id,
        module: simulation.module,
      });
      setLaunchingId(simulation.id);
      const launchUrl = await buildScholarlabLaunchUrl(currentUser.id, simulation);
      window.open(launchUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message || 'Failed to launch Scholarlab simulation.');
    } finally {
      setLaunchingId(null);
    }
  };

  return (
    <main className="scholarlab-page">
      <section className="scholarlab-toolbar">
        <div className="scholarlab-title">
          <span className="scholarlab-kicker">Interactive simulations</span>
          <h1>Scholarlab</h1>
          <p>
            {loading
              ? 'Loading the simulation library...'
              : `${filteredSimulations.length} of ${simulations.length} simulations`}
          </p>
        </div>
        <div className="scholarlab-actions">
          <label className="scholarlab-search">
            <span>Search</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search modules, topics, or IDs"
            />
          </label>
        </div>
      </section>

      <nav className="scholarlab-subjects" aria-label="Subjects">
        {subjects.map((subject) => (
          <button
            key={subject}
            className={subject === activeSubject ? 'active' : ''}
            onClick={() => setActiveSubject(subject)}
            type="button"
          >
            {subject}
          </button>
        ))}
      </nav>

      {error && (
        <section className="scholarlab-state scholarlab-state--error">
          <div>
            <h2>Could not load Scholarlab</h2>
            <p>{error === 'Failed to fetch' ? 'The browser could not reach the backend API. Please check the backend deployment and try again.' : error}</p>
          </div>
          <button type="button" onClick={loadSimulations}>Retry</button>
        </section>
      )}

      {loading ? (
        <div className="scholarlab-grid">
          {Array.from({ length: 8 }).map((_, index) => (
            <div className="scholarlab-card loading" key={index} />
          ))}
        </div>
      ) : filteredSimulations.length > 0 ? (
        <div className="scholarlab-grid">
          {filteredSimulations.map((simulation) => (
            <article className="scholarlab-card" key={simulation.id || simulation.webglUrl}>
              <div className="scholarlab-thumb">
                {simulation.iconUrl ? (
                  <img src={simulation.iconUrl} alt="" loading="lazy" />
                ) : (
                  <span>{simulation.customUseCaseId || 'SL'}</span>
                )}
              </div>
              <div className="scholarlab-card-body">
                <div className="scholarlab-card-meta">
                  <span>{simulation.customUseCaseId || 'Simulation'}</span>
                  {simulation.hasObservation && <span>Observation</span>}
                </div>
                <h2>{simulation.module}</h2>
                <p>{simulation.description}</p>
                <div className="scholarlab-tags">
                  {getTopicList(simulation.topics).map((topic) => (
                    <span key={topic}>{topic}</span>
                  ))}
                </div>
              </div>
              <button
                className="scholarlab-launch"
                onClick={() => openSimulation(simulation)}
                disabled={launchingId === simulation.id}
                type="button"
              >
                {launchingId === simulation.id ? 'Opening...' : 'Open'}
              </button>
            </article>
          ))}
        </div>
      ) : !error ? (
        <section className="scholarlab-state">
          <div>
            <h2>No simulations found</h2>
            <p>Try another search term or switch back to all subjects.</p>
          </div>
          <button type="button" onClick={() => { setQuery(''); setActiveSubject('All'); }}>
            Clear filters
          </button>
        </section>
      ) : null}
    </main>
  );
}
